// -----------------------------------------------------------------------------
// API contract types — keep in lockstep with src/DocuSense.Api/**/*.cs
// Wave 2-5 extend this file with the document-types, documents, connections,
// webhooks and dashboard shapes as those endpoints come online.
// -----------------------------------------------------------------------------

/** API path prefix shared by every authenticated endpoint. */
export const API_PREFIX = '/api/v1' as const;

/** Endpoint URL constants. Wave 2-5 must append here, not inline strings. */
export const Endpoints = {
  authToken: `${API_PREFIX}/auth/token`,
  licenseStatus: `${API_PREFIX}/license/status`,
  health: `${API_PREFIX}/health`,
  documents: `${API_PREFIX}/documents`,
  documentTypes: `${API_PREFIX}/document-types`,
  connections: `${API_PREFIX}/connections`,
  ocrProviders: `${API_PREFIX}/ocr-providers`,
  webhooks: `${API_PREFIX}/webhooks`,
  alertPreferences: `${API_PREFIX}/alerts/preferences`,
  branding: `${API_PREFIX}/branding`,
} as const;

// ---------- Auth ----------

export interface TokenRequest {
  tenantId: string;
  apiKey: string;
}

/** Mirrors AuthEndpoints.TokenResponse (camelCase JSON). */
export interface TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

export interface AuthErrorResponse {
  error: string;
}

/** License tier as emitted in the JWT `license_type` claim. Arbitrary string — no longer a fixed enum. */
export type LicenseType = string;

/** Decoded JWT payload (claims we read on the client). */
export interface JwtPayload {
  tenant_id: string;
  user_id: string;
  license_type: LicenseType;
  /** Serialized as JSON array string in the token; decoded by AuthProvider. */
  scopes: string[];
  exp: number;
  iat?: number;
  sub?: string;
  jti?: string;
}

// ---------- Scopes ----------

export const Scopes = {
  DocumentsRead: 'documents:read',
  DocumentsWrite: 'documents:write',
  SchemasRead: 'schemas:read',
  SchemasWrite: 'schemas:write',
  ConnectionsRead: 'connections:read',
  ConnectionsWrite: 'connections:write',
  Admin: 'admin',
  /** Cross-tenant platform-debug scope — only granted to platform operators. */
  PlatformDebug: 'platform:debug',
  /** Platform-admin panel scope — granted only to tokens from POST /api/v1/admin/auth/token. */
  PlatformAdmin: 'platform:admin',
} as const;
export type Scope = (typeof Scopes)[keyof typeof Scopes];

// ---------- Error shapes ----------

/** RFC 7807 ProblemDetails — emitted by the API for 401/404/422 etc. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Present on 422 validation responses. */
  errors?: Record<string, string[]>;
}

/** 402 Payment Required body for license-limit exhaustion. */
export interface LicenseLimitExceeded {
  code: 'license_limit_exceeded' | string;
  message: string;
  limit: number;
  current: number;
}

/** 403 Forbidden body when a feature is gated by the tenant plan. */
export interface LicenseFeatureUnavailable {
  code: 'license_feature_unavailable' | string;
  message: string;
}

// ---------- License (mirrors LicenseStatusResponse + LicenseFeaturesResponse on the API) ----------

/** Per-plan feature flags exposed alongside the usage counters. */
export interface LicenseFeatures {
  /** Null on Enterprise (unlimited). */
  documentTypesLimit: number | null;
  allowsLocalAiProviders: boolean;
  allowsWebhooks: boolean;
  /** Pre-formatted retention window (e.g. "30 days"). */
  jobRetention: string;
  /** Platform-admin privilege: may this tenant choose the OCR source of its document types? */
  canEditOcrSource: boolean;
  /** Opt-in (plan flag ± tenant override): may this tenant upload without picking a type and let the classifier resolve it? */
  autoClassificationEnabled: boolean;
}

/**
 * Mirrors `DocuSense.Application.UseCases.Licensing.LicenseStatusResponse`.
 * `documentsLimit` is null on Enterprise (unlimited). `resetsAt` is always
 * the first day of the next UTC month at 00:00:00Z.
 */
export interface LicenseStatusResponse {
  licenseType: LicenseType;
  documentsUsed: number;
  documentsLimit: number | null;
  resetsAt: string;
  features: LicenseFeatures;
}
